import { z } from 'zod'

import { MAX_WINDOWS } from './window-defaults.ts'
import type { DesktopState } from './window-store.ts'

/**
 * As janelas que ficam guardadas neste computador — F138.
 *
 * ## Por aparelho, por pessoa e por conta
 *
 * A chave leva o usuário e a conta (`tenantId`), e mora no `localStorage`:
 *
 * - **por aparelho**, porque posição e tamanho dependem do monitor — o layout
 *   do PC do balcão não cabe no notebook do escritório;
 * - **por pessoa**, porque o caixa e o dono usam o mesmo computador com
 *   janelas diferentes;
 * - **por conta**, porque a mesma pessoa em duas lojas abre telas diferentes.
 *
 * ## O que se guarda, e o que não
 *
 * O caminho da tela, sem a busca — um `?editar=` de ontem não reabre uma ficha
 * hoje —, a geometria, o estado e a ordem da pilha. **Não** se guardam janelas
 * filhas nem formulário pela metade: reabrir o Aeris devolve as telas, não o
 * que estava sendo digitado.
 *
 * ## O que se descarta ao ler
 *
 * Tudo que não é exatamente a versão 1: JSON quebrado, versão desconhecida,
 * mais janelas que o limite, caminho que não começa com uma barra só, e as
 * telas de fora do shell (login, trocar senha, servidor). Um layout salvo por
 * uma versão anterior do produto não pode derrubar a área de trabalho.
 */

export const LAYOUT_VERSION = 1

const OUTSIDE_SHELL: readonly string[] = [
  '/login',
  '/trocar-senha',
  '/servidor',
]

const SavedWindowSchema = z.object({
  pathname: z.string().startsWith('/').max(200),
  // `z.number()` já recusa infinito e NaN no zod 4.
  rect: z.object({
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
  }),
  mode: z.enum(['normal', 'minimized', 'maximized']),
  wasMaximized: z.boolean(),
})

const SavedLayoutSchema = z.object({
  version: z.literal(LAYOUT_VERSION),
  /** Da janela de trás para a da frente. */
  windows: z.array(SavedWindowSchema).max(MAX_WINDOWS),
  /** Qual delas estava ativa, pela posição em `windows`. */
  activeIndex: z.number().int().nonnegative().nullable(),
})

export type SavedWindow = z.infer<typeof SavedWindowSchema>
export type SavedLayout = z.infer<typeof SavedLayoutSchema>

export const layoutKey = (userId: string, tenantId: string): string =>
  `aeris.desktop-layout.v${String(LAYOUT_VERSION)}:${userId}:${tenantId}`

const pathOf = (href: string): string => href.split(/[?#]/)[0] ?? href

/** O estado da área de trabalho, no formato que vai para o disco. */
export const serializeLayout = (state: DesktopState): SavedLayout => {
  const ordered = [...state.windows].sort((a, b) => a.z - b.z)
  const active = ordered.findIndex((entry) => entry.id === state.activeId)

  return {
    version: LAYOUT_VERSION,
    windows: ordered.map((entry) => ({
      pathname: pathOf(entry.href),
      rect: entry.rect,
      mode: entry.mode,
      wasMaximized: entry.wasMaximized,
    })),
    activeIndex: active === -1 ? null : active,
  }
}

/** O que foi guardado, ou `null` quando não há nada que dê para confiar. */
export const parseLayout = (raw: string | null): SavedLayout | null => {
  if (raw === null) return null

  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return null
  }

  const parsed = SavedLayoutSchema.safeParse(json)
  if (!parsed.success) return null

  const kept = parsed.data.windows.filter(
    (entry) =>
      !entry.pathname.startsWith('//') &&
      !OUTSIDE_SHELL.includes(entry.pathname)
  )
  const active =
    parsed.data.activeIndex === null
      ? null
      : parsed.data.windows[parsed.data.activeIndex]

  const activeIndex =
    active === undefined || active === null ? null : kept.indexOf(active)

  return {
    version: LAYOUT_VERSION,
    windows: kept,
    activeIndex:
      activeIndex === null || activeIndex === -1 ? null : activeIndex,
  }
}

/** Lê do `localStorage`, que pode nem existir (modo privado, bloqueado). */
export const readLayout = (key: string): SavedLayout | null => {
  try {
    return parseLayout(window.localStorage.getItem(key))
  } catch {
    return null
  }
}

/** Grava; um disco cheio ou um armazenamento bloqueado não derrubam a tela. */
export const writeLayout = (key: string, layout: SavedLayout): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(layout))
  } catch {
    // Sem onde guardar, a área de trabalho só não volta como estava.
  }
}
